"use client";

import Image from "next/image";
import Link from "next/link";
import { useRef, useState, useEffect } from "react";

import { testimonials } from "@/config/landing";
import { HeaderSection } from "@/components/shared/header-section";
import { Button } from "@/components/ui/button";
import { Instagram, Linkedin, ExternalLink } from "lucide-react";

interface TestimonialsProps {
  /** When provided, only these testimonial ids are shown, in this exact order, as a static grid. */
  ids?: string[];
  label?: string;
  title?: string;
  subtitle?: string;
}

export default function Testimonials({ ids, label, title, subtitle }: TestimonialsProps = {}) {
  // Curated mode: pick the requested testimonials in order and render a static grid.
  if (ids && ids.length > 0) {
    const curated = ids
      .map((id) => testimonials.find((t) => t.id === id))
      .filter((t): t is NonNullable<typeof t> => Boolean(t));

    return (
      <section className="bg-muted/30 py-8 sm:py-16">
        <div className="container max-w-6xl mb-10">
          <HeaderSection
            label={label ?? "Testimonials"}
            title={title ?? "What our clients are sharing."}
            subtitle={subtitle ?? "Discover the glowing feedback from our delighted customers worldwide."}
          />
        </div>

        <div className="container max-w-6xl grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {curated.map((item) => (
            <TestimonialCard key={item.id || item.name} item={item} />
          ))}
        </div>

        <div className="container max-w-6xl flex justify-center mt-10">
          <Link href="https://discord.gg/vDsrkmdAqS" target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#5865F2] hover:bg-[#4752C4] text-white">
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.27 5.33C17.94 4.71 16.5 4.26 15 4a.09.09 0 0 0-.07.03c-.18.33-.39.76-.53 1.09a16.09 16.09 0 0 0-4.8 0c-.14-.34-.35-.76-.54-1.09-.01-.02-.04-.03-.07-.03-1.5.26-2.93.71-4.27 1.33-.01 0-.02.01-.03.02-2.72 4.07-3.47 8.03-3.1 11.95 0 .02.01.04.03.05 1.8 1.32 3.53 2.12 5.24 2.65.03.01.06 0 .07-.02.4-.55.76-1.13 1.07-1.74.02-.04 0-.08-.04-.09-.57-.22-1.11-.48-1.64-.78-.04-.02-.04-.08-.01-.11.11-.08.22-.17.33-.25.02-.02.05-.02.07-.01 3.44 1.57 7.15 1.57 10.55 0 .02-.01.05-.01.07.01.11.09.22.17.33.26.04.03.04.09-.01.11-.52.31-1.07.56-1.64.78-.04.01-.05.06-.04.09.32.61.68 1.19 1.07 1.74.03.01.06.02.09.01 1.72-.53 3.45-1.33 5.25-2.65.02-.01.03-.03.03-.05.44-4.53-.73-8.46-3.1-11.95-.01-.01-.02-.02-.04-.02zM8.52 14.91c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12 0 1.17-.84 2.12-1.89 2.12zm6.97 0c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12 0 1.17-.83 2.12-1.89 2.12z"/>
              </svg>
              Join our Discord
            </Button>
          </Link>
        </div>
      </section>
    );
  }

  // Split testimonials into 2 rows based on text length
  // Row 1: Short testimonials (< 150 characters)
  // Row 2: Longer testimonials (>= 150 characters)
  const row1 = testimonials.filter(t => {
    const textOnly = t.review.replace(/<[^>]*>/g, '');
    return textOnly.length < 150;
  });
  const row2 = testimonials.filter(t => {
    const textOnly = t.review.replace(/<[^>]*>/g, '');
    return textOnly.length >= 150;
  });

  return (
    <section className="bg-muted/30 py-8 sm:py-16">
      <div className="container max-w-6xl mb-10">
        <HeaderSection
          label={label ?? "Testimonials"}
          title={title ?? "What our clients are sharing."}
          subtitle={subtitle ?? "Discover the glowing feedback from our delighted customers worldwide."}
        />
      </div>

      {/* Row 1 - Fast scrolling - Full width */}
      <div className="mb-10">
        <TestimonialRow items={row1} speedPxPerSecond={60} />
      </div>

      {/* Row 2 - Slower scrolling - Full width */}
      <div className="mb-10">
        <TestimonialRow items={row2} speedPxPerSecond={40} />
      </div>
      
      <div className="container max-w-6xl flex justify-center">
        <Link href="https://discord.gg/vDsrkmdAqS" target="_blank" rel="noopener noreferrer">
          <Button className="bg-[#5865F2] hover:bg-[#4752C4] text-white">
            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.27 5.33C17.94 4.71 16.5 4.26 15 4a.09.09 0 0 0-.07.03c-.18.33-.39.76-.53 1.09a16.09 16.09 0 0 0-4.8 0c-.14-.34-.35-.76-.54-1.09-.01-.02-.04-.03-.07-.03-1.5.26-2.93.71-4.27 1.33-.01 0-.02.01-.03.02-2.72 4.07-3.47 8.03-3.1 11.95 0 .02.01.04.03.05 1.8 1.32 3.53 2.12 5.24 2.65.03.01.06 0 .07-.02.4-.55.76-1.13 1.07-1.74.02-.04 0-.08-.04-.09-.57-.22-1.11-.48-1.64-.78-.04-.02-.04-.08-.01-.11.11-.08.22-.17.33-.25.02-.02.05-.02.07-.01 3.44 1.57 7.15 1.57 10.55 0 .02-.01.05-.01.07.01.11.09.22.17.33.26.04.03.04.09-.01.11-.52.31-1.07.56-1.64.78-.04.01-.05.06-.04.09.32.61.68 1.19 1.07 1.74.03.01.06.02.09.01 1.72-.53 3.45-1.33 5.25-2.65.02-.01.03-.03.03-.05.44-4.53-.73-8.46-3.1-11.95-.01-.01-.02-.02-.04-.02zM8.52 14.91c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12 0 1.17-.84 2.12-1.89 2.12zm6.97 0c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12 0 1.17-.83 2.12-1.89 2.12z"/>
            </svg>
            Join our Discord
          </Button>
        </Link>
      </div>
    </section>
  );
}

function renderSocialIconStatic(iconType: string) {
  switch (iconType) {
    case "instagram":
      return <Instagram style={{ color: '#E1306C' }} className="h-4 w-4" />;
    case "linkedin":
      return <Linkedin style={{ color: '#0A66C2' }} className="h-4 w-4" />;
    case "trustpilot":
      return <ExternalLink className="h-4 w-4" />;
    default:
      return <ExternalLink className="h-4 w-4" />;
  }
}

function getCountryFlagStatic(country: string) {
  switch (country) {
    case "Croatia":
      return "🇭🇷";
    case "USA":
      return "🇺🇸";
    case "UK":
      return "🇬🇧";
    case "Korea":
      return "🇰🇷";
    default:
      return "";
  }
}

/** Static (non-scrolling) testimonial card used for curated grids. */
function TestimonialCard({ item }: { item: any }) {
  return (
    <div className="h-full">
      <div className="relative h-full overflow-hidden rounded-xl border border-border/60 bg-gradient-to-r from-zinc-100/70 via-background to-zinc-100/50 dark:from-zinc-900/80 dark:via-background dark:to-zinc-900/60">
        <div className="h-px bg-gradient-to-r from-transparent via-zinc-300 dark:via-zinc-600 to-transparent" />
        <div className="flex flex-col px-4 py-5 sm:p-6">
          <div className="relative mb-4 flex items-center gap-3">
            {item.image && (
              <span className="relative inline-flex size-10 shrink-0 items-center justify-center rounded-full text-base">
                <Image
                  width={100}
                  height={100}
                  className="size-full rounded-full border"
                  src={item.image}
                  alt={item.name}
                  unoptimized={true}
                />
              </span>
            )}
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <p className="text-md font-medium text-foreground">
                  {item.name} {item.country && <span className="ml-1">{getCountryFlagStatic(item.country)}</span>}
                </p>
                <div className="flex gap-2">
                  {item.socialLink && (
                    <Link href={item.socialLink} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                      {renderSocialIconStatic(item.socialIcon || "external-link")}
                    </Link>
                  )}
                  {item.secondarySocialLink && (
                    <Link href={item.secondarySocialLink} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                      {renderSocialIconStatic(item.secondarySocialIcon || "external-link")}
                    </Link>
                  )}
                </div>
              </div>
              <p className="text-sm text-muted-foreground">{item.job}</p>
            </div>
          </div>
          <p className="text-muted-foreground" dangerouslySetInnerHTML={{ __html: item.review }}></p>
        </div>
      </div>
    </div>
  );
}

function TestimonialRow({ items, speedPxPerSecond }: { items: any[], speedPxPerSecond: number }) {
  const sequenceRef = useRef<HTMLDivElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  const [positionX, setPositionX] = useState(0);
  const positionRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartXRef = useRef<number | null>(null);
  const [sequenceWidth, setSequenceWidth] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const measure = () => {
      const width = sequenceRef.current?.offsetWidth || 0;
      setSequenceWidth(width);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    positionRef.current = positionX;
  }, [positionX]);

  useEffect(() => {
    const tick = (now: number) => {
      if (!lastTimeRef.current) lastTimeRef.current = now;
      const dt = (now - lastTimeRef.current) / 1000;
      lastTimeRef.current = now;

      if (!isPaused && !isDragging && sequenceWidth > 0) {
        let next = positionRef.current - speedPxPerSecond * dt;
        if (Math.abs(next) >= sequenceWidth) {
          next += sequenceWidth;
        }
        setPositionX(next);
        positionRef.current = next;
      }

      animationRef.current = requestAnimationFrame(tick);
    };

    animationRef.current = requestAnimationFrame(tick);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    };
  }, [isPaused, isDragging, sequenceWidth, speedPxPerSecond]);

  const onPointerDown = (clientX: number) => {
    setIsPaused(true);
    setIsDragging(true);
    dragStartXRef.current = clientX;
  };

  const onPointerMove = (clientX: number) => {
    if (!isDragging || dragStartXRef.current === null) return;
    const delta = clientX - dragStartXRef.current;
    dragStartXRef.current = clientX;
    const next = positionRef.current + delta;
    setPositionX(next);
    positionRef.current = next;
  };

  const onPointerUp = () => {
    setIsDragging(false);
    setIsPaused(false);
    dragStartXRef.current = null;
  };

  const renderTestimonialCard = (item: any) => (
    <div className="inline-block w-[350px] mx-3" key={`${item.name}-card`}>
      <div className="relative rounded-xl border bg-muted/25 h-full">
        <div className="flex flex-col px-4 py-5 sm:p-6">
          <div>
            <div className="relative mb-4 flex items-center gap-3">
              {item.image && <span className="relative inline-flex size-10 shrink-0 items-center justify-center rounded-full text-base">
                <Image
                  width={100}
                  height={100}
                  className="size-full rounded-full border"
                  src={item.image}
                  alt={item.name}
                  unoptimized={true}
                />
              </span>}
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <p className="text-md font-medium text-foreground">
                    {item.name} {item.country && <span className="ml-1">{getCountryFlag(item.country)}</span>}
                  </p>
                  <div className="flex gap-2">
                    {item.socialLink && (
                      <Link href={item.socialLink} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                        {renderSocialIcon(item.socialIcon || "external-link")}
                      </Link>
                    )}
                    {item.secondarySocialLink && (
                      <Link href={item.secondarySocialLink} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                        {renderSocialIcon(item.secondarySocialIcon || "external-link")}
                      </Link>
                    )}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  {item.job}
                </p>
              </div>
            </div>
            <p className="text-muted-foreground" dangerouslySetInnerHTML={{ __html: item.review }}></p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSocialIcon = (iconType: string) => {
    switch (iconType) {
      case "instagram":
        return <Instagram style={{ color: '#E1306C' }} className="h-4 w-4" />;
      case "linkedin":
        return <Linkedin style={{ color: '#0A66C2' }} className="h-4 w-4" />;
      case "trustpilot":
        return <ExternalLink className="h-4 w-4" />;
      default:
        return <ExternalLink className="h-4 w-4" />;
    }
  };

  const getCountryFlag = (country: string) => {
    switch (country) {
      case "Croatia":
        return "🇭🇷";
      case "USA":
        return "🇺🇸";
      case "UK":
        return "🇬🇧";
      case "Korea":
        return "🇰🇷";
      default:
        return "";
    }
  };

  return (
    <div className="relative overflow-hidden">
      <div
        className="relative overflow-hidden select-none cursor-grab active:cursor-grabbing"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => { setIsPaused(false); setIsDragging(false); onPointerUp(); }}
        onMouseDown={(e) => onPointerDown(e.clientX)}
        onMouseMove={(e) => onPointerMove(e.clientX)}
        onMouseUp={onPointerUp}
        onTouchStart={(e) => onPointerDown(e.touches[0].clientX)}
        onTouchMove={(e) => onPointerMove(e.touches[0].clientX)}
        onTouchEnd={onPointerUp}
      >
        <div
          className="flex items-center will-change-transform"
          style={{ transform: `translateX(${positionX}px)` }}
        >
          <div ref={sequenceRef} className="flex items-center">
            {items.map((item) => renderTestimonialCard(item))}
          </div>
          {/* Duplicate for continuous loop */}
          <div className="flex items-center">
            {items.map((item) => (
              <div key={`dup-${item.name}`}>
                {renderTestimonialCard(item)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
